import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  LIVER_KIDNEY_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for AZ Appreciate Chronic Kidney Disease Study
 * Target population: People of African descent with chronic kidney disease and APOL-1 genotype
 */
export const azAppreciateScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "chronic_kidney_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with Chronic Kidney Disease by a medical professional?",
    helpText: "Chronic kidney disease (CKD) is diagnosed when there is evidence of long term kidney damage or reduced kidney function, and is usually monitored with blood and urine tests",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_ckd",
        label: "Yes, I have been diagnosed with CKD",
      },
      {
        value: "no_ckd",
        label: "No, I have not been diagnosed with CKD",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is for people who have been diagnosed with chronic kidney disease.",
    attributeMapping: mappers.value({
      yes_ckd: [
        { attributeDefinitionId: 23333, booleanAttributeValue: true }, // has_CKD - chronic kidney disease as diagnosed by medical professional
      ],
      no_ckd: [
        { attributeDefinitionId: 23333, booleanAttributeValue: false }, // has_CKD
      ],
    }),
  },

  {
    id: "age",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "65_or_under",
        label: "65 or under",
      },
      {
        value: "66_to_70",
        label: "66 to 70",
      },
      {
        value: "71_or_older",
        label: "71 or older",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is only available to participants 70 years of age or younger.",
    attributeMapping: mappers.value({
      "65_or_under": [],
      "66_to_70": [],
      "71_or_older": [],
    }),
  },

  {
    id: "race_ethnicity", 
    type: QuestionType.RADIO,
    question: "Do you have African ancestry?",
    helpText: "This includes people whose family origins are from countries in Sub-Saharan Africa or the Caribbean - for example Nigeria, Ghana, Haiti or Jamaica.  People with African ancestry are more likely to carry a gene called APOL-1, which this study is researching",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_african_descent",
        label: "Yes",
      },
      {
        value: "no_african_descent", 
        label: "No",
        disqualify: true,
      },
      {
        value: "prefer_not_answer",
        label: "I'd prefer not to answer",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is looking into genetic factors that primarily affect people of African descent.",
    attributeMapping: mappers.value({
      yes_african_descent: [
        { attributeDefinitionId: 26748, radioAttributeValue: "000-kiodu" }, // Yes - has_African_ancestry
      ],
      no_african_descent: [
        { attributeDefinitionId: 26748, radioAttributeValue: "001-n5whup" }, // No - has_African_ancestry
      ],
      prefer_not_answer: [
        { attributeDefinitionId: 26748, radioAttributeValue: "002-30v3pa" }, // Not sure / Prefer not to answer
      ],
    }),
  },

  {
    id: "dialysis_transplant",
    type: QuestionType.RADIO,
    question:
      "Have you ever been on dialysis, or has your doctor talked to you about dialysis or a kidney transplant as a next step?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "on_dialysis_now",
        label: "Yes, I am on dialysis now",
        disqualify: true,
      },
      {
        value: "had_transplant",
        label: "Yes, I have had a kidney transplant",
        disqualify: true,
      },
      {
        value: "on_waitlist",
        label: "Yes, I have been told I will need dialysis or a kidney transplant soon",
        disqualify: true,
      },
      {
        value: "none_above",
        label: "No, none of the above",
      },
    ],
    disqualifyMessage:
      "This study is not suitable for people who are on dialysis, have had a kidney transplant, are on the transplant waiting list, or whose doctor has referred them for dialysis or transplant.",
    attributeMapping: mappers.value({
      on_dialysis_now: [
        { attributeDefinitionId: 20254, booleanAttributeValue: true }, // has_endstage_kidney_disease
        { attributeDefinitionId: 20342, booleanAttributeValue: true }, // has_current_dialysis
        { attributeDefinitionId: 15039, booleanAttributeValue: false }, // had_transplant
        { attributeDefinitionId: 396, booleanAttributeValue: false }, // had_kidneytransplant
      ],
      had_transplant: [
        { attributeDefinitionId: 15039, booleanAttributeValue: true }, // had_transplant
        { attributeDefinitionId: 396, booleanAttributeValue: true }, // had_kidneytransplant
        { attributeDefinitionId: 20254, booleanAttributeValue: false }, // has_endstage_kidney_disease
        { attributeDefinitionId: 20342, booleanAttributeValue: false }, // has_current_dialysis
        { attributeDefinitionId: 8683, booleanAttributeValue: false }, // kidneytransplant_waitlist
      ],
      on_waitlist: [
        { attributeDefinitionId: 8683, booleanAttributeValue: true }, // kidneytransplant_waitlist — only set this; dialysis/transplant status unknown
      ],
      none_above: [
        { attributeDefinitionId: 20254, booleanAttributeValue: false }, // has_endstage_kidney_disease
        { attributeDefinitionId: 20342, booleanAttributeValue: false }, // has_current_dialysis
        { attributeDefinitionId: 15039, booleanAttributeValue: false }, // had_transplant
        { attributeDefinitionId: 396, booleanAttributeValue: false }, // had_kidneytransplant
        { attributeDefinitionId: 8683, booleanAttributeValue: false }, // kidneytransplant_waitlist
      ],
    }),
  },

  {
    id: "kidney_disease_cause",
    type: QuestionType.RADIO,
    question: "Is your chronic kidney disease caused by any of the following?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "lupus_nephritis",
        label: "Lupus",
        disqualify: true,
      },
      {
        value: "iga_nephropathy",
        label: "IgA nephropathy (autoimmune disease diagnosed with a biopsy)",
        disqualify: true,
      },
      {
        value: "none_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "This study is looking for specific causes of chronic kidney disease. Your kidney disease cause may not be suitable for this particular study.",
    attributeMapping: mappers.value({
      lupus_nephritis: [
        { attributeDefinitionId: 21985, booleanAttributeValue: true }, // SLE/lupus
      ],
      iga_nephropathy: [
        { attributeDefinitionId: 26683, booleanAttributeValue: true }, // IgA nephropathy
      ],
      none_above: [
        { attributeDefinitionId: 21985, booleanAttributeValue: false }, // SLE/lupus nephritis
        { attributeDefinitionId: 26683, booleanAttributeValue: false }, // IgA nephropathy
      ],
    }),
  },

  {
    ...standardHeightQuestion,
    helpText: "We'll calculate your BMI using your height and weight",
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    helpText: "Used to calculate BMI",
    maxBMI: 50,
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "diabetes",
    type: QuestionType.RADIO,
    question: "Do you have diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type2_diabetes",
        label: "I have type 2 diabetes",
        disqualify: (allAnswers) => allAnswers["age"] === "66_to_70",
        disqualifyMessage: "This study is only available to participants with type 2 diabetes who are 65 years of age or younger.",
      },
      {
        value: "type1_diabetes",
        label: "I have type 1 diabetes",
        disqualify: true,
      },
      {
        value: "no_diabetes",
        label: "I don't have diabetes",
        skipNextQuestions: 1, // Skip insulin experience question
      },
    ],
    disqualifyMessage: "This study cannot include people with type 1 diabetes.",
    attributeMapping: mappers.value({
      type2_diabetes: [
        { attributeDefinitionId: 702, booleanAttributeValue: true }, // has_diabetes_type2
      ],
      type1_diabetes: [
        { attributeDefinitionId: 1356, booleanAttributeValue: true }, // has_diabetes_type1
      ],
      no_diabetes: [
        { attributeDefinitionId: 702, booleanAttributeValue: false }, // has_diabetes_type2
        { attributeDefinitionId: 1356, booleanAttributeValue: false }, // has_diabetes_type1
      ],
    }),
  },

  {
    id: "insulin_experience",
    type: QuestionType.RADIO,
    question: "Which of the following best describes your experience with insulin?",
    helpText:
      "Insulin is a medication for diabetes given by injection, pen, or pump. Examples include Tresiba, Humalog, Lantus, NovoLog, and Levemir.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "currently_taking",
        label: "I am currently taking insulin",
        disqualify: true,
      },
      {
        value: "never_taken",
        label: "I have never taken insulin",
      },
      {
        value: "past_less_than_3_months",
        label: "I have taken insulin in the past for less than 3 months total",
      },
      {
        value: "past_3_months_or_more",
        label: "I have taken insulin in the past for 3 months or more",
        disqualify: true,
      },
      {
        value: "unsure",
        label: "I am unsure",
      },
    ],
    disqualifyMessage:
      "This study cannot include people who are currently taking insulin or who have taken insulin for more than 3 months.",
    attributeMapping: mappers.value({
      // On insulin currently
      currently_taking: [
        { attributeDefinitionId: 23574, booleanAttributeValue: true }, // has_insulin_daily - currently using insulin daily
        { attributeDefinitionId: 26773, booleanAttributeValue: false }, // insulin_less_than_3_months (N/A when current)
      ],
      // Not on insulin; never took it (do not set 26773 - would wrongly imply took for 3+ months)
      never_taken: [
        { attributeDefinitionId: 23574, booleanAttributeValue: false }, // has_insulin_daily - not currently on insulin
      ],
      // Not on insulin currently; took in the past for <3 months total
      past_less_than_3_months: [
        { attributeDefinitionId: 23574, booleanAttributeValue: false }, // has_insulin_daily - not currently on insulin
        { attributeDefinitionId: 26773, booleanAttributeValue: true }, // insulin_less_than_3_months - past use was <3 months
      ],
      // Not on insulin currently; took in the past for 3+ months (disqualifies)
      past_3_months_or_more: [
        { attributeDefinitionId: 23574, booleanAttributeValue: false }, // has_insulin_daily - not currently on insulin
        { attributeDefinitionId: 26773, booleanAttributeValue: false }, // insulin_less_than_3_months - no, used 3+ months
      ],
      unsure: [],
    }),
  },

  {
    id: "medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "hepatitis_b_c",
        label: "Hepatitis B or C that has NOT been treated",
        disqualify: true,
      },
      {
        value: "sickle_cell",
        label: "Sickle cell disease",
        disqualify: true,
      },
      {
        value: "liver_disease",
        label: "Liver disease, liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "long_qt",
        label: "Long QT syndrome",
        disqualify: true,
      },
      {
        value: "bleeding_disorder",
        label: "Bleeding disorder such as hemophilia or Von Willebrand's disease",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "Cancer diagnosed or treated within the past 5 years (excluding SCC and BCC skin cancers)",
        disqualify: true,
      },
      {
        value: "none_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        hepatitis_b_c: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
        ],
        sickle_cell: [
          { attributeDefinitionId: 25006, booleanAttributeValue: true }, // sickle cell
        ],
        liver_disease: [
          { attributeDefinitionId: 21026, booleanAttributeValue: true }, // liver disease
        ],
        long_qt: [
          { attributeDefinitionId: 23739, booleanAttributeValue: true }, // long qt
        ],
        bleeding_disorder: [
          { attributeDefinitionId: 15061, booleanAttributeValue: true }, // bleeding disorder
        ],
        cancer_5_years: [
          { attributeDefinitionId: 25310, booleanAttributeValue: true }, // cancer 5 years
        ],
      },
      noneAttributeMap: {
        none_above: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
          { attributeDefinitionId: 25006, booleanAttributeValue: false }, // sickle cell
          { attributeDefinitionId: 21026, booleanAttributeValue: false }, // liver disease
          { attributeDefinitionId: 23739, booleanAttributeValue: false }, // long qt
          { attributeDefinitionId: 15061, booleanAttributeValue: false }, // bleeding disorder
          { attributeDefinitionId: 25310, booleanAttributeValue: false }, // cancer 5 years
        ],
      },
    }),
  },

  {
    id: "recent_medical_events",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "stroke_tia_3months",
        label: "I've had a stroke or a TIA (mini-stroke) in the past 3 months",
        disqualify: true,
      },
      {
        value: "heart_attack_6months",
        label: "I've had a heart attack in the past 6 months",
        disqualify: true,
      },
      {
        value: "coronary_procedure_6months",
        label: "I've had a coronary artery bypass graft (CABG) or coronary artery stenting in the past 6 months",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I've had an organ or bone marrow transplant",
        disqualify: true,
      },
      {
        value: "none_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with these recent medical events or current conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        stroke_tia_3months: [
          { attributeDefinitionId: 1370, booleanAttributeValue: true }, // had_CVA_3months
        ],
        heart_attack_6months: [
          { attributeDefinitionId: 6775, booleanAttributeValue: true }, // had_heartattack_6months
        ],
        coronary_procedure_6months: [
          { attributeDefinitionId: 25414, booleanAttributeValue: true }, // has_history_coronary_artery_revascularization_6_months
        ],
        organ_transplant: [
          { attributeDefinitionId: 15039, booleanAttributeValue: true }, // had_transplant
        ],
      },
      noneAttributeMap: {
        none_above: [
          { attributeDefinitionId: 1370, booleanAttributeValue: false }, // had_CVA_3months
          { attributeDefinitionId: 6775, booleanAttributeValue: false }, // had_heartattack_6months
          { attributeDefinitionId: 25414, booleanAttributeValue: false }, // has_history_coronary_artery_revascularization_6_months
          { attributeDefinitionId: 15039, booleanAttributeValue: false }, // had_transplant
        ],
      },
    }),
  },

  {
    id: "kidney_monitoring_12months",
    type: QuestionType.RADIO,
    question:
      "In the past 12 months have you had blood and urine tests done as part of monitoring your kidney disease?",
    required: true,
    allowBack: true,
    disqualifyMessage:
      "This study requires that participants have had kidney disease monitoring (blood and urine tests) by a healthcare provider in the past 12 months, or have a scheduled appointment.",
    options: [
      {
        value: "yes",
        label: "Yes, I have had blood and urine tests done in the past 12 months",
      },
      {
        value: "no",
        label: "No, I have not had blood and urine tests done in the past 12 months",
        disqualify: true,
      },
      {
        value: "scheduled_appointment",
        label: "No, but I have a scheduled appointment",
      },
      {
        value: "unsure",
        label: "I am unsure",
      },
    ],
    attributeMapping: mappers.value({
      yes: [],
      no: [],
      scheduled_appointment: [],
      unsure: [],
    }),
  },

  {
    id: "clinical_trial_participation",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_recent_trial",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no_recent_trial",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include people who have recently participated in other clinical trials.",
    attributeMapping: mappers.value({
      yes_recent_trial: [
        { attributeDefinitionId: 23777, booleanAttributeValue: true }, // clinical trial participation
      ],
      no_recent_trial: [
        { attributeDefinitionId: 23777, booleanAttributeValue: false }, // clinical trial participation
      ],
    }),
  },

];